import { createSlice } from '@reduxjs/toolkit';
import { axiosInstance } from '../Axios';

const getAxiosConfig = (token) => ({
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
  },
});

const getErrorMessage = (error) =>
  error.response && error.response.data.message ? error.response.data.message : error.message;

const ProjectEmployeeReducer = createSlice({
  name: 'projectEmployee',
  initialState: {
    projectEmployeeLoading: false,
    projectEmployeeList: null,
    projectEmployeeSingle: null,
    projectEmployeeError: null,
    projectEmployeeSuccessMessage: '',
  },
  reducers: {
    projectEmployeeRequest(state) {
      state.projectEmployeeLoading = true;
      state.projectEmployeeError = null;
      state.projectEmployeeSuccessMessage = '';
    },
    projectEmployeeListSuccess(state, action) {
      state.projectEmployeeLoading = false;
      state.projectEmployeeList = action.payload;
      state.projectEmployeeError = null;
    },
    projectEmployeeSingleSuccess(state, action) {
      state.projectEmployeeLoading = false;
      state.projectEmployeeSingle = action.payload;
      state.projectEmployeeError = null;
    },
    projectEmployeeActionSuccess(state, action) {
      state.projectEmployeeLoading = false;
      state.projectEmployeeError = null;
      state.projectEmployeeSuccessMessage = action.payload || 'Project employee saved successfully';
    },
    projectEmployeeFail(state, action) {
      state.projectEmployeeLoading = false;
      state.projectEmployeeError = action.payload;
    },
    clearProjectEmployeeSingle(state) {
      state.projectEmployeeSingle = null;
    },
  },
});

export const {
  projectEmployeeRequest,
  projectEmployeeListSuccess,
  projectEmployeeSingleSuccess,
  projectEmployeeActionSuccess,
  projectEmployeeFail,
  clearProjectEmployeeSingle,
} = ProjectEmployeeReducer.actions;

export const getProjectEmployeeListDispatch = (form, token) => async (dispatch) => {
  try {
    dispatch(projectEmployeeRequest());
    const { data } = await axiosInstance.get('/ProjectEmployee/GetProjectEmployeeList', {
      ...getAxiosConfig(token),
      params: {
        ProjectId: form?.ProjectId || form?.projectId || 0,
        EmployeeName: form?.EmployeeName || form?.employeeName || '',
      },
    });

    dispatch(projectEmployeeListSuccess(data));
  } catch (error) {
    dispatch(projectEmployeeFail(getErrorMessage(error)));
  }
};

export const getProjectEmployeeByIdDispatch = (projectEmployeeId, token) => async (dispatch) => {
  try {
    dispatch(projectEmployeeRequest());
    const { data } = await axiosInstance.get('/ProjectEmployee/GetProjectEmployeeById', {
      ...getAxiosConfig(token),
      params: { projectEmployeeId },
    });

    dispatch(projectEmployeeSingleSuccess(data));
    return data;
  } catch (error) {
    const message = getErrorMessage(error);
    dispatch(projectEmployeeFail(message));
    return { status: 0, message };
  }
};

export const createProjectEmployeeTransactionDispatch = (projectEmployeeData, token) => async (dispatch) => {
  try {
    dispatch(projectEmployeeRequest());
    const { data } = await axiosInstance.post(
      '/ProjectEmployee/CreateProjectEmployee',
      projectEmployeeData,
      getAxiosConfig(token)
    );

    if (data.status === 1) {
      dispatch(projectEmployeeActionSuccess(data.message || 'Project employee created successfully'));
    } else {
      dispatch(projectEmployeeFail(data.message || 'Unable to create project employee'));
    }

    return data;
  } catch (error) {
    const message = getErrorMessage(error);
    dispatch(projectEmployeeFail(message));
    return { status: 0, message };
  }
};

export const updateProjectEmployeeDispatch = (projectEmployeeData, token) => async (dispatch) => {
  try {
    dispatch(projectEmployeeRequest());
    const { data } = await axiosInstance.post(
      '/ProjectEmployee/UpdateProjectEmployee',
      projectEmployeeData,
      getAxiosConfig(token)
    );

    if (data.status === 1) {
      dispatch(projectEmployeeActionSuccess(data.message || 'Project employee updated successfully'));
    } else {
      dispatch(projectEmployeeFail(data.message || 'Unable to update project employee'));
    }

    return data;
  } catch (error) {
    const message = getErrorMessage(error);
    dispatch(projectEmployeeFail(message));
    return { status: 0, message };
  }
};

export const deleteProjectEmployeeDispatch = (projectEmployeeId, token) => async (dispatch) => {
  try {
    dispatch(projectEmployeeRequest());
    const { data } = await axiosInstance.post('/ProjectEmployee/DeleteProjectEmployee', '', {
      ...getAxiosConfig(token),
      params: { projectEmployeeId },
    });

    if (!data || data.status === 1) {
      dispatch(projectEmployeeActionSuccess(data?.message || 'Project employee deleted successfully'));
    } else {
      dispatch(projectEmployeeFail(data.message || 'Unable to delete project employee'));
    }

    return data || { status: 1 };
  } catch (error) {
    const message = getErrorMessage(error);
    dispatch(projectEmployeeFail(message));
    return { status: 0, message };
  }
};

export default ProjectEmployeeReducer;
